import { SupStoredTxSettings, type SupStoredTxSettingsServices } from "@/store/types";
import useSupported from "@/hooks/api/useSupported";
import { useMemo } from "react";

export const TxSettingsDictionary = {
  bridges: {
    title: "TOKEN BRIDGES",
    min: 1,
  },
  ambs: {
    title: "DATA BRIDGES",
    min: 2,
  },
  dexes: {
    title: "DEXS",
    min: 1,
  },
  liquidity_providers: {
    title: "LIQUIDITY PROVIDERS",
    min: 1,
  },
} as Record<
  SupStoredTxSettingsServices,
  {
    title: string;
    min: number;
  }
>;

interface useValidateTxSettingsReturn {
  isLoading: boolean;
  isValid: undefined | boolean;
  validatedSettings: SupStoredTxSettings | undefined;
}

/**
 * Hook used to validate TX settings based on the supported services
 *
 * @todo: This hook should be used to validate the settings of the TX flow in calculate queries
 * @param {SupStoredTxSettings} settingsToValidate
 * @returns {useValidateTxSettingsReturn}
 */
export default function useValidateTxSettings(settingsToValidate: SupStoredTxSettings): useValidateTxSettingsReturn {
  const { data, isLoading } = useSupported();
  return useMemo(() => {
    if (!data) {
      return {
        isLoading,
        isValid: undefined,
        validatedSettings: undefined,
      };
    }
    const isValid = Object.entries(TxSettingsDictionary).reduce((acc, [key, value]) => {
      return acc && data[key as SupStoredTxSettingsServices].length - settingsToValidate.services[key as SupStoredTxSettingsServices].length >= value.min;
    }, true);

    const validatedSettings = {
      ...settingsToValidate,
      services: {
        ambs: settingsToValidate.services.ambs.filter(serviceId => data.ambs.findIndex(service => service.key === serviceId) !== -1),
        dexes: settingsToValidate.services.dexes.filter(serviceId => data.dexes.findIndex(service => service.key === serviceId) !== -1),
        bridges: settingsToValidate.services.bridges.filter(serviceId => data.bridges.findIndex(service => service.key === serviceId) !== -1),
        liquidity_providers: settingsToValidate.services.liquidity_providers.filter(serviceId => data.liquidity_providers.findIndex(service => service.key === serviceId) !== -1),
      },
    } as SupStoredTxSettings;

    return {
      isLoading,
      isValid,
      validatedSettings,
    };
  }, [data, isLoading, settingsToValidate]);
}
